import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowUturnLeftIcon, CheckBadgeIcon, CheckCircleIcon, EyeIcon } from "@heroicons/react/24/outline";
import { toast } from "../utils/toast";
import { bursariesApi } from "../api/hliLists";
import { errorMessageOf } from "../api/scholarships";
import { ConfirmModal, Container, PageHeader, RowAction, Select, Table } from "./ui";
import { selectionColumns } from "./selection/settingsColumns";
import { SELECTIONS_KEY } from "./SelectionComponent";
import { BURSARIES_KEY, useYearState } from "./bursaries/shared";
import { INSTITUTIONS_PATH } from "./bursaries/InstitutionBursariesPages";

// What each action does, for the confirmation dialog
const ACTIONS = {
  VERIFY: { label: "Verify", past: "verified", description: "Mark this selection as verified so it can be approved." },
  APPROVE: { label: "Approve", past: "approved", description: "Approve this selection: its grants reach the students and it can no longer change." },
  RETURN: { label: "Return", past: "returned", description: "Send this selection back for corrections.", destructive: true },
};

// Each institution's selection per study area, and its verify → approve review
export default function LocalLoanApproveStep() {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [year, setYear, years] = useYearState();
  const [pending, setPending] = useState(null);

  const { data: selections = [], isPending } = useQuery({
    queryKey: [...SELECTIONS_KEY, year],
    enabled: Boolean(year),
    queryFn: async () => {
      try {
        return (await bursariesApi.selections({ academicYearId: year })).data;
      } catch (error) {
        console.error("Failed to fetch selections:", error);
        toast.error(errorMessageOf(error, "Failed to fetch the selections."), { id: "selections-error" });
        throw error;
      }
    },
  });

  const review = useMutation({
    mutationFn: ({ action, id }) => bursariesApi.reviewSelection(id, action),
    onSuccess: (_, { action }) => {
      toast.success(`Selection ${ACTIONS[action].past}.`);
      queryClient.invalidateQueries({ queryKey: [BURSARIES_KEY] });
    },
    onError: (error) => {
      console.error("Failed to review the selection:", error);
      toast.error(errorMessageOf(error, "The selection was not updated."));
    },
    onSettled: () => setPending(null),
  });

  const viewRequests = (row) => nav(`${INSTITUTIONS_PATH}/${row.institution.id}?year=${row.academicYear.id}`);

  const columns = [
    ...selectionColumns(true),
    {
      key: "actions",
      label: <span className="sr-only">Actions</span>,
      exportable: false,
      render: (row) => (
        <div className="flex items-center gap-1 whitespace-nowrap">
          <RowAction label="View requests" variant="ghost" Icon={EyeIcon} onClick={() => viewRequests(row)} />
          {row.status === "INITIATED" && (
            <RowAction label="Verify" variant="soft" Icon={CheckBadgeIcon} onClick={() => setPending({ action: "VERIFY", row })} />
          )}
          {row.status === "VERIFIED" && (
            <RowAction label="Approve" variant="solid" Icon={CheckCircleIcon} onClick={() => setPending({ action: "APPROVE", row })} />
          )}
          {(row.status === "INITIATED" || row.status === "VERIFIED") && (
            <RowAction label="Return" variant="danger-soft" Icon={ArrowUturnLeftIcon} onClick={() => setPending({ action: "RETURN", row })} />
          )}
        </div>
      ),
    },
  ];

  const action = pending && ACTIONS[pending.action];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Selection Approval Levels"
        subtitle="Verify, then approve, each selection; approval publishes its grants to the students"
        actions={<Select aria-label="Academic year" value={year} onChange={(e) => setYear(e.target.value)} options={years} className="w-44" />}
      />

      <Table cardView viewKey="loan-approval-selections"
        columns={columns}
        data={selections}
        isLoading={isPending && Boolean(year)}
        searchPlaceholder="Search by institution or study area"
        exportName="selections"
        emptyTitle="No selection set for this year"
      />

      <ConfirmModal
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        onConfirm={() => review.mutate({ action: pending.action, id: pending.row.id })}
        loading={review.isPending}
        title={action ? `${action.label} the ${pending.row.studyArea} selection of ${pending.row.institution.name}?` : ""}
        description={action?.description}
        confirmLabel={action?.label}
        destructive={action?.destructive}
      />
    </Container>
  );
}
